/**
 * The interface that is used to keep the variables used in the .env file or any env related functionality.
 *
 * Local development reads relative API bases from `.env.development`. Production builds
 * do not load that file, so these fields stay on the absolute URLs in `.env`.
 */
class EnvService {

    // #############################  APPLICATION  #############################

    /**
     * Default Title of the application
     */
    static websiteName: string = process.env.REACT_APP_WEBSITE_NAME ?? ''

    /**
     * Default Description of the application
     */
    static websiteDescription: string = process.env.REACT_APP_WEBSITE_DESCRIPTION ?? ''

    /**
     * The Twitter creator handle of the application.
     */
    static websiteTwitterCreator: string = process.env.REACT_APP_WEBSITE_TWITTER_CREATOR ?? ''

    /**
     * The timezone of the api.
     */
    static apiTimezone: string = process.env.REACT_APP_API_TIMEZONE ?? 'UTC'

    /**
     * The timezone of the api.
     */
    static defaultLocale: string = process.env.REACT_APP_DEFAULT_LOCALE ?? 'en'

    /**
     * The name of the application for ui-components cache.
     */
    static uiComponentsApplicationName: string = process.env.REACT_APP_UI_COMPONENTS_APPLICATION_NAME ?? ''

    // #############################  URLS  #############################

    /**
     * The Public Url of the application.
     */
    static PublicUrl: string = process.env.PUBLIC_URL ?? "";
    /**
     * The full url of our api, including the base url and the api version.
     */
    static FullApiUrl: string = process.env.REACT_APP_API_URL ?? "";
    /**
     * The base url of our api. In development this is the `/Operator/api` path on localhost.
     */
    static ApiUrl: string = process.env.REACT_APP_API_URL ?? "";
    /**
     * The base url of client apis. In development this is the `/Client` path on localhost.
     * Callers append `/api/...`, which the dev-server proxy forwards.
     */
    static ClientApiUrl: string = process.env.REACT_APP_CLIENT_API_BASE_URL ?? "";

    /**
     * The Base url of admin apis.
     * @deprecated
     */
    static AdminApiUrl: string = process.env.REACT_APP_ADMIN_BASE_URL
        ? process.env.REACT_APP_ADMIN_BASE_URL + 'api/'
        : "";
    /**
     * The Base url of the files uploaded to the admin server.
     * @deprecated
     */
    static AdminFileBaseUrl: string = process.env.REACT_APP_ADMIN_BASE_URL
        ? process.env.REACT_APP_ADMIN_BASE_URL + 'files/'
        : "";
    /**
     * The Live url for connecting to BizLive runtime server.
     */
    static LiveUrl: string = process.env.REACT_APP_LIVE_URL ?? "";

    /**
     * The base url of the virtual printer api.
     */
    static VirtualPrinterApiUrl: string = process.env.REACT_APP_VIRTUAL_PRINTER_API_URL ?? "";

    /**
     * The base url of the tasks api.
     */
    static TasksApiUrl: string = process.env.REACT_APP_TASKS_API_URL ?? "";

    /**
     * The base url of the notes api.
     */
    static NotesApiUrl = process.env.REACT_APP_NOTES_API_URL ?? "";

    /**
     * The base url of the NCR api.
     */
    static NcrApiUrl = process.env.REACT_APP_NCR_API_URL ?? "";

    /**
     * The base url of the BizFile api.
     */
    static BizFileApiUrl = process.env.REACT_APP_BIZ_FILE_API_URL ?? "";

    /**
     * The base url of the Biz production api.
     */
    static BizProductionApiUrl = process.env.REACT_APP_BIZ_PRODUCTION_API_URL ?? "";

    /**
     * BizForm API base URL for NCR-related flows (forms embedded in NCR).
     */
    static BizFormNcrApiUrl: string = process.env.REACT_APP_BIZ_FORM_NCR_API_URL ?? "";

    /**
     * BizForm API base URL for app-wide bootstrap (e.g. BizFile token on cold start / login).
     * In development this stays on the `/Operator/api` path so it uses the same dev-server proxy.
     */
    static BizFormOperatorAppApiUrl: string = process.env.REACT_APP_BIZ_FORM_OPERATOR_APP_API_URL ?? "";

    /**
     * The base url of the component state api.
     */
    static componentStateApiUrl: string = process.env.REACT_APP_COMPONENT_STATE_API_URL ?? "";

    /**
     * The application Urls that the user is allowed to access through this app.
     */
    static ApplicationUrls = {
        bizCms: process.env.REACT_APP_BIZ_CMS_URL ?? '' as const,
        bizInventory: process.env.REACT_APP_BIZ_INVENTORY_URL ?? '' as const,
        bizLearn: process.env.REACT_APP_BIZ_LEARN_URL ?? '' as const,
        bizQrCode: process.env.REACT_APP_BIZ_QRCODE_URL ?? '' as const,
        bizProduction: process.env.REACT_APP_BIZ_PRODUCTION_URL ?? '' as const,
        bizHr: process.env.REACT_APP_BIZ_HR_URL ?? '' as const,
        bizCrm: process.env.REACT_APP_BIZ_CRM_URL ?? '' as const,
        bizCore: process.env.REACT_APP_BIZ_CORE_URL ?? '' as const,
    }

    // #############################  MISC  #############################

    static EncryptionSecretKey: string = process.env.REACT_APP_ENCRYPTION_SECRET_KEY ?? '';

    /**
     * The VAPID key used in the firebase notification system.
     */
    static NotificationVAPIDKey: string = process.env.REACT_APP_NOTIFICATION_VAPID_KEY ?? "";

    /**
     * The id of the crisp service for this website.
     */
    static crispWebsiteID: string = process.env.REACT_APP_CRISP_WEBSITE_ID ?? '';

    /**
     * The id of the clarity service for this website.
     */
    static clarityProjectId: string = process.env.REACT_APP_CLARITY_PROJECT_ID ?? '';

    /**
     * Determines if the current environment is development.
     */
    static isDevelopment: boolean = !!process.env.NODE_ENV && process.env.NODE_ENV === 'development';

    /**
     * Determines if the current environment is Test.
     */
    static isTest: boolean = process.env.NODE_ENV === 'test';

    /**
     * Determines if the current environment is production.
     */
    static isProduction: boolean = process.env.NODE_ENV === 'production';

    /**
     * The title of the USA country.
     */
    static USATitle: string = process.env.REACT_APP_USA_TITLE ?? "";

    /**
     * Whether to bypass the authentication in the application.
     */
    static bypassAuth: boolean = process.env.REACT_APP_BYPASS_AUTH === 'true';

    /**
     * The base url of the demo api is the same as the demo api.
     */
    static get showInDemoEnvironment(): boolean {
        if (this.disableNonProductionEnvWarning) {
            return false;
        }
        return this.FullApiUrl.startsWith(process.env.REACT_APP_API_TEST_URL);
    }

    /**
     * Whether the environment is the main environment.
     */
    static get inMainEnvironment(): boolean {
        return this.FullApiUrl.startsWith(process.env.REACT_APP_API_PROD_URL);
    }

    /**
     * The base url of the main api is the same as the main api.
     */
    static get showInMainEnvironment(): boolean {
        if (this.disableNonProductionEnvWarning) {
            return false;
        }
        return this.inMainEnvironment && !this.isProduction;
    }

    /**
     * Whether to disable the non-production environment warning in the application.
     */
    static disableNonProductionEnvWarning: boolean = process.env.REACT_APP_DISABLE_NON_PRODUCTION_ENV_WARNING === 'true';

    /**
     * Whether to enable the demo name masking in the application, which masks the names of customers, vendors, and products in the demo environment for privacy reasons.
     */
    static enableDemoNameMasking: boolean = process.env.REACT_APP_ENABLE_DEMO_NAME_MASKING === 'true';


    // ############################# Users & Partners  #############################

    /**
     * The user ids that are defined in the application.
     */
    static DefinedUserIds = {
        demo: process.env.REACT_APP_USER_ID_DEMO ?? '',
        aiSandbox: process.env.REACT_APP_USER_ID_AISANDBOX ?? '',
    }

    /**
     * The partner ids that are defined in the application.
     */
    static DefinedPartnerIds = {
        demo: process.env.REACT_APP_PARTNER_ID_DEMO ?? '',
        chtp: process.env.REACT_APP_PARTNER_ID_CHTP ?? '',
        chtpEdmonton: process.env.REACT_APP_PARTNER_ID_CHTP_EDMONTON ?? '',
        jmac: process.env.REACT_APP_PARTNER_ID_JMAC ?? '',
        truxparts: process.env.REACT_APP_PARTNER_ID_TRUXPARTS ?? '',
        horsebarn: process.env.REACT_APP_PARTNER_ID_HORSEBARN ?? '',
        myers: process.env.REACT_APP_PARTNER_ID_MYERS ?? '',
        cascade: process.env.REACT_APP_PARTNER_ID_CASCADE ?? '',
        autoPartDemo: process.env.REACT_APP_PARTNER_ID_AUTO_PART_DEMO ?? '',
        sinkut: process.env.REACT_APP_PARTNER_ID_SINKUT ?? '',
        roppelIndustries: process.env.REACT_APP_PARTNER_ID_ROPPEL_INDUSTRIES ?? '',
    }

    // ############################# In-Development Features  #############################

    /**
     * Injects a mock image carousel row into homepage edition data for local testing.
     * Enabled by default in development unless REACT_APP_MOCK_HOME_IMAGE_CAROUSEL=false.
     */
    static MockHomeImageCarousel: boolean = process.env.REACT_APP_MOCK_HOME_IMAGE_CAROUSEL === 'true'
        || (process.env.NODE_ENV === 'development' && process.env.REACT_APP_MOCK_HOME_IMAGE_CAROUSEL !== 'false');

    /**
     * Whether to use the auth blocker in the application, which blocks the user from using the application if there is an authentication issue such as token expiration, and shows a dialog to prompt the user to re-authenticate.
     */
    static DEV_useAuthBlocker: boolean = process.env.REACT_APP_DEV_USE_AUTH_BLOCKER === 'true' || process.env.NODE_ENV === 'development';
    /**
     * Whether to show the fast connection feature in the application, which allows users to quickly connect to their accounts by scanning a QR code with their mobile devices, instead of entering their credentials manually.
     */
    static DEV_showFastConnection: boolean = process.env.REACT_APP_DEV_SHOW_FAST_CONNECTION === 'true';
    /**
     * Whether to show the purchase feature in the application.
     */
    static DEV_showPurchase: boolean = process.env.REACT_APP_DEV_SHOW_PURCHASE === 'true';
    /**
     * Whether to show the vendors feature in the application.
     */
    static DEV_showVendors: boolean = process.env.REACT_APP_DEV_SHOW_VENDORS === 'true';
    /**
     * Whether to show the competitor price reports feature in the application.
     */
    static DEV_showCompetitorPriceReports: boolean = process.env.REACT_APP_DEV_SHOW_COMPETITOR_PRICE_REPORTS === 'true';
    /**
     * Shows the damage control feature in the application.
     */
    static DEV_showDamageControl: boolean = process.env.REACT_APP_DEV_SHOW_DAMAGE_CONTROL === 'true';
    /**
     * Shows the inventory transfer feature in the application.
     */
    static DEV_showInventoryTransfer: boolean = process.env.REACT_APP_DEV_SHOW_INVENTORY_TRANSFER === 'true';
    /**
     * Uses Warehouse v2 (CleanAF/ReadAllWarehouse) in inventory transfer create and list labels.
     * Enable when the backend returns warehouse `id` on that endpoint.
     */
    static DEV_useWarehouseV2InTransfers: boolean = process.env.REACT_APP_DEV_USE_WAREHOUSE_V2_IN_TRANSFERS === 'true';
    /**
     * Shows Settings → Import dataset for set inventory availability (Inventory/SetInventoryAvailability).
     */
    static DEV_showSetInventoryAvailability: boolean = process.env.REACT_APP_DEV_SHOW_SET_INVENTORY_AVAILABILITY === 'true';
    /**
     * Shows the CMS feature in the application.
     */
    static DEV_showCMSFeature: boolean = process.env.REACT_APP_DEV_SHOW_CMS_FEATURE === 'true';
    /**
     * Shows the customer login logs feature in the application.
     */
    static DEV_showCustomerLoginLogs: boolean = process.env.REACT_APP_DEV_SHOW_CUSTOMER_LOGIN_LOGS === 'true';
    /**
     * Shows the email link in the print section.
     */
    static DEV_showBizPrintEmailLink: boolean = process.env.REACT_APP_DEV_SHOW_BIZ_PRINT_EMAIL_LINK === 'true';
    /**
     * Shows the customer deletion feature in the application.
     */
    static DEV_showVinDecoder: boolean = process.env.REACT_APP_DEV_SHOW_VIN_DECODER === 'true';
    /**
     * Shows the multi language support feature in the application.
     */
    static DEV_multiLanguageSupport: boolean = process.env.REACT_APP_DEV_MULTI_LANGUAGE_SUPPORT === 'true';
    /**
     * Shows the feature that allows showing products on backorders in the application.
     */
    static DEV_showProductOnBackorders: boolean = process.env.REACT_APP_DEV_SHOW_PRODUCT_ON_BACKORDERS === 'true';

    /**
     * Form management (BizForm list / responses admin). Off until backend and rollout are ready.
     */
    static DEV_showFormManagement: boolean = process.env.REACT_APP_DEV_SHOW_FORM_MANAGEMENT === 'true';

    /**
     * BizPoint partner loyalty + admin portal. Off until backend and rollout are ready.
     */
    static DEV_showBizPoint: boolean = process.env.REACT_APP_DEV_SHOW_BIZ_POINT === 'true';

    /**
     * Support package (Contact Us Messages, etc.). Off until ready for rollout.
     */
    static DEV_showSupport: boolean = process.env.REACT_APP_DEV_SHOW_SUPPORT === 'true';

    /**
     * Dynamic pages management under User Platform. Off until backend and rollout are ready.
     */
    static DEV_showDynamicPages: boolean = process.env.REACT_APP_DEV_SHOW_DYNAMIC_PAGES === 'true';

    /**
     * Shows per-line product comments on order, quote, invoice, and credit product tables and print views.
     */
    static DEV_showOrderProductComments: boolean = process.env.REACT_APP_DEV_SHOW_ORDER_PRODUCT_COMMENTS === 'true';

    /**
     * Allows editing the order-level terms code on quote, sales order, and invoice information pages.
     */
    static DEV_editOrderTermsCode: boolean = process.env.REACT_APP_DEV_EDIT_ORDER_TERMS_CODE === 'true';
    /**
     * Enables region rates, region rate management, and freight provider organizational settings.
     */
    static DEV_freightEstimation: boolean = process.env.REACT_APP_DEV_FREIGHT_ESTIMATION === 'true';

    /**
     * Rollout gate for dual-mode app-bar search (Nexus autocomplete + System command palette).
     * When false, only the legacy Nexus autocomplete field is used (hidden on the Nexus page).
     * When true, operators can toggle modes via an endAdornment; default mode is Nexus.
     */
    static DEV_showPanelSearchCommand: boolean = process.env.REACT_APP_DEV_SHOW_PANEL_SEARCH_COMMAND === 'true';

    /**
     * Shows the Submit Ticket action on API error toasts. Off until the reporting flow is ready for rollout.
     */
    static DEV_showErrorTicketSubmission: boolean = process.env.REACT_APP_DEV_SHOW_ERROR_TICKET_SUBMISSION === 'true';

}

export default EnvService;
